import { onScopeDispose, ref } from "vue";
import { getPaymentOutcome, PaymentOutcomeContractError, UnsafeRefError, } from "@/services/donationFormService";
import { logger } from "@/services/loggerService";
// CONSTRAINT: every read draws on the per-IP token bucket the form read, the submit and the funnel beacons
// also draw on. Shorter or more delays can empty it for donors sharing an IP.
const POLL_DELAYS_MS = [0, 1000, 2000, 3000, 5000, 8000, 13000];
const RETRIED_CLIENT_STATUSES = [408, 429];
function httpStatus(e) {
    const status = e?.response?.status;
    return typeof status === "number" ? status : null;
}
function isRetried(status) {
    return status === null || status >= 500 || RETRIED_CLIENT_STATUSES.includes(status);
}
export function usePaymentOutcome(slug, donationRef) {
    const outcome = ref("Pending");
    const controller = new AbortController();
    let timer;
    let wake = () => { };
    onScopeDispose(() => {
        controller.abort();
        clearTimeout(timer);
        wake();
    });
    function wait(ms) {
        return new Promise((resolve) => {
            wake = resolve;
            timer = setTimeout(resolve, ms);
        });
    }
    async function poll() {
        if (donationRef === null) {
            return;
        }
        for (const delay of POLL_DELAYS_MS) {
            if (delay > 0) {
                await wait(delay);
            }
            if (controller.signal.aborted) {
                return;
            }
            try {
                const next = await getPaymentOutcome(slug, donationRef, controller.signal);
                if (controller.signal.aborted) {
                    return;
                }
                outcome.value = next;
                if (next !== "Pending") {
                    return;
                }
            }
            catch (e) {
                if (controller.signal.aborted) {
                    return;
                }
                if (e instanceof UnsafeRefError) {
                    logger.error("Payment outcome read failed", { slug, status: "unsafe-ref" });
                    return;
                }
                if (e instanceof PaymentOutcomeContractError) {
                    logger.error("Payment outcome read failed", { slug, status: "contract" });
                    return;
                }
                const status = httpStatus(e);
                logger.error("Payment outcome read failed", { slug, status: status ?? "network" });
                if (!isRetried(status)) {
                    return;
                }
            }
        }
    }
    return { outcome, settled: poll() };
}
